// Feature: docs/features/nma-compliance-dashboard.md
import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Select,
  Flex,
  Input,
  Button,
  HStack,
  Badge,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Progress,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import { useContext } from 'react';
import HeaderMenus from './HeaderMenus';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const NMAComplianceDashboard = () => {
  const { accessToken } = useContext(AppContext);
  const [data, setData] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // Store all categories for the dropdown
  const [allTechnologists, setAllTechnologists] = useState([]);
  const [allSurgeons, setAllSurgeons] = useState([]);
  const [allFacilities, setAllFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [startDate, setStartDate] = useState('2026-05-10');
  const [endDate, setEndDate] = useState('');
  const [technologistFilter, setTechnologistFilter] = useState('');
  const [surgeonFilter, setSurgeonFilter] = useState('');
  const [facilityFilter, setFacilityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (accessToken) {
      fetchData();
    }
  }, [accessToken]);

  const fetchData = async (overrides = {}) => {
    setLoading(true);
    setError(null);
    try {
      if (!accessToken) return;
      
      // Build query string
      const params = new URLSearchParams();
      const p_startDate = overrides.startDate !== undefined ? overrides.startDate : startDate;
      const p_endDate = overrides.endDate !== undefined ? overrides.endDate : endDate;
      const p_tech = overrides.technologist !== undefined ? overrides.technologist : technologistFilter;
      const p_surgeon = overrides.surgeon !== undefined ? overrides.surgeon : surgeonFilter;
      const p_facility = overrides.facility !== undefined ? overrides.facility : facilityFilter;
      const p_category = overrides.category !== undefined ? overrides.category : categoryFilter;

      if (p_startDate) params.append('start_date', p_startDate);
      if (p_endDate) params.append('end_date', p_endDate);
      if (p_tech) params.append('technologist', p_tech);
      if (p_surgeon) params.append('surgeon', p_surgeon);
      if (p_facility) params.append('facility', p_facility);
      if (p_category) params.append('category', p_category);

      const queryString = params.toString();
      const endpoint = queryString ? `/api/nma/compliance?${queryString}` : '/api/nma/compliance';

      const response = await callApi(endpoint, accessToken, { method: 'GET' });
      
      if (response.ok) {
        const responseData = await response.json();
        if (responseData.status === 'success') {
          const fetchedData = responseData.data || [];
          setData(fetchedData);
          
          // Only update the categories list if we aren't currently filtering by category
          // This prevents the dropdown options from disappearing when a category is selected
          if (!categoryFilter) {
            const categories = [...new Set(fetchedData.map(item => item.procedure_category).filter(Boolean))].sort();
            setAllCategories(categories);
          }
          if (!technologistFilter) {
            const techs = [...new Set(fetchedData.map(item => item.technologist_name).filter(Boolean))].sort();
            setAllTechnologists(techs);
          }
          if (!surgeonFilter) {
            const surgeons = [...new Set(fetchedData.map(item => item.referring_provider).filter(Boolean))].sort();
            setAllSurgeons(surgeons);
          }
          if (!facilityFilter) {
            const facilities = [...new Set(fetchedData.map(item => item.facility).filter(Boolean))].sort();
            setAllFacilities(facilities);
          }
        } else {
          setError(responseData.message || 'Failed to fetch compliance data');
        }
      } else {
        setError(`Failed to fetch compliance data: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error fetching compliance data:', err);
      setError('An error occurred while fetching data.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleClearFilters = () => {
    setStartDate('2026-05-10');
    setEndDate('');
    setTechnologistFilter('');
    setSurgeonFilter('');
    setFacilityFilter('');
    setCategoryFilter('');
    fetchData({
      startDate: '2026-05-10',
      endDate: '',
      technologist: '',
      surgeon: '',
      facility: '',
      category: ''
    });
  };

  const handleTechnologistClick = (techName) => {
    setTechnologistFilter(techName);
    setTabIndex(0); // Switch to Raw Data tab
    fetchData({ technologist: techName });
  };

  const exportToCSV = () => {
    if (!data || data.length === 0) return;

    const headers = [
      'Case ID',
      'Case Link',
      'Case Description (Cadwell/Scheduling)',
      'Template Name (Summary Report)',
      'Template Version (Summary Report)',
      'Has Cadwell Data',
      'Date of Surgery',
      'Technologist',
      'Surgeon',
      'Facility',
      'Category',
      'Naming',
      '5-Muscle',
      'Failure Reason'
    ];

    const escapeCSV = (str) => {
      if (str === null || str === undefined) return '""';
      const stringified = String(str);
      // Escape quotes by doubling them, and wrap in quotes if there are commas, quotes, or newlines
      if (stringified.includes(',') || stringified.includes('"') || stringified.includes('\n')) {
        return `"${stringified.replace(/"/g, '""')}"`;
      }
      return `"${stringified}"`;
    };

    const csvRows = data.map(row => [
      escapeCSV(row.tech_report_case_id || row.external_case_id),
      escapeCSV(`https://app.revedy.io/ionmform?caseid=${row.external_case_id}`),
      escapeCSV(row.case_description),
      escapeCSV(row.template_name),
      escapeCSV(row.template_version),
      row.has_cadwell_data ? 'Yes' : 'No',
      escapeCSV(row.date_of_surgery),
      escapeCSV(row.technologist_name),
      escapeCSV(row.referring_provider),
      escapeCSV(row.facility),
      escapeCSV(row.procedure_category),
      row.naming_compliant ? 'Yes' : 'No',
      row.procedure_category === 'Spine' ? (row.muscle_compliant ? 'Yes' : 'No') : 'N/A',
      escapeCSV(row.muscle_justification)
    ].join(','));

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `NMA_Compliance_Export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate Naming Summary (All Cases)
  const getNamingSummary = () => {
    const summary = {};
    
    data.forEach(row => {
      const tech = row.technologist_name || 'Unknown';
      if (!summary[tech]) {
        summary[tech] = {
          name: tech,
          totalCases: 0,
          namingFailures: 0
        };
      }
      
      summary[tech].totalCases += 1;
      if (!row.naming_compliant) summary[tech].namingFailures += 1;
    });

    // Convert to array and calculate % correct
    return Object.values(summary).map(tech => ({
      ...tech,
      namingCorrectRate: (((tech.totalCases - tech.namingFailures) / tech.totalCases) * 100).toFixed(1)
    })).sort((a, b) => b.totalCases - a.totalCases); // Sort by volume descending
  };

  // Calculate Spine 5-Muscle Summary (Spine Cases Only)
  const getSpineMuscleSummary = () => {
    const summary = {};
    
    data.filter(row => row.procedure_category === 'Spine').forEach(row => {
      const tech = row.technologist_name || 'Unknown';
      if (!summary[tech]) {
        summary[tech] = {
          name: tech,
          totalCases: 0,
          muscleFailures: 0
        };
      }
      
      summary[tech].totalCases += 1;
      if (!row.muscle_compliant) summary[tech].muscleFailures += 1;
    });

    // Convert to array and calculate % correct
    return Object.values(summary).map(tech => ({
      ...tech,
      muscleCorrectRate: (((tech.totalCases - tech.muscleFailures) / tech.totalCases) * 100).toFixed(1)
    })).sort((a, b) => b.totalCases - a.totalCases); // Sort by volume descending
  };

  const namingSummaryData = getNamingSummary();
  const spineSummaryData = getSpineMuscleSummary();

  // Calculate Daily Summary
  const getDailySummary = () => {
    const summary = {};
    
    data.forEach(row => {
      const date = row.date_of_surgery || 'Unknown';
      if (!summary[date]) {
        summary[date] = {
          date: date,
          totalCases: 0,
          namingFailures: 0,
          spineCases: 0,
          muscleFailures: 0
        };
      }
      
      summary[date].totalCases += 1;
      if (!row.naming_compliant) summary[date].namingFailures += 1;
      
      if (row.procedure_category === 'Spine') {
        summary[date].spineCases += 1;
        if (!row.muscle_compliant) summary[date].muscleFailures += 1;
      }
    });

    return Object.values(summary).map(day => ({
      ...day,
      namingCorrectRate: Number((((day.totalCases - day.namingFailures) / day.totalCases) * 100).toFixed(1)),
      muscleCorrectRate: day.spineCases > 0 ? Number((((day.spineCases - day.muscleFailures) / day.spineCases) * 100).toFixed(1)) : null
    })).sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort oldest first for charts
  };

  const dailySummaryData = getDailySummary();

  // Calculate Overall KPIs for the top of the page
  const getOverallStats = () => {
    if (data.length === 0) return null;
    
    let namingFails = 0;
    let spineTotal = 0;
    let spineFails = 0;
    
    data.forEach(row => {
      if (!row.naming_compliant) namingFails++;
      if (row.procedure_category === 'Spine') {
        spineTotal++;
        if (!row.muscle_compliant) spineFails++;
      }
    });
    
    return {
      totalCases: data.length,
      namingCorrectRate: (((data.length - namingFails) / data.length) * 100).toFixed(1),
      spineCases: spineTotal,
      muscleCorrectRate: spineTotal > 0 ? (((spineTotal - spineFails) / spineTotal) * 100).toFixed(1) : 'N/A'
    };
  };

  const overallStats = getOverallStats();

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1400px" margin="0 auto">

        <Box bg="white" p={4} borderRadius="md" shadow="sm" mb={6} borderWidth="1px">
        <form onSubmit={handleFilterSubmit}>
          <Flex gap={4} flexWrap="wrap" alignItems="flex-end">
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>Start Date</Text>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} size="sm" />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>End Date</Text>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} size="sm" />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>Technologist</Text>
              <Select value={technologistFilter} onChange={(e) => setTechnologistFilter(e.target.value)} size="sm" placeholder="All Technologists">
                {allTechnologists.map(tech => (
                  <option key={tech} value={tech}>{tech}</option>
                ))}
              </Select>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>Surgeon</Text>
              <Select value={surgeonFilter} onChange={(e) => setSurgeonFilter(e.target.value)} size="sm" placeholder="All Surgeons">
                {allSurgeons.map(surg => (
                  <option key={surg} value={surg}>{surg}</option>
                ))}
              </Select>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>Facility</Text>
              <Select value={facilityFilter} onChange={(e) => setFacilityFilter(e.target.value)} size="sm" placeholder="All Facilities">
                {allFacilities.map(fac => (
                  <option key={fac} value={fac}>{fac}</option>
                ))}
              </Select>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>Category</Text>
              <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} size="sm" placeholder="All Categories">
                {allCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Select>
            </Box>
            <HStack>
              <Button type="submit" colorScheme="blue" size="sm">Apply Filters</Button>
              <Button onClick={handleClearFilters} variant="outline" size="sm">Clear</Button>
              <Button onClick={exportToCSV} colorScheme="green" size="sm" isDisabled={data.length === 0}>Export CSV</Button>
            </HStack>
          </Flex>
        </form>
      </Box>

      {error && (
        <Box bg="red.100" color="red.700" p={3} borderRadius="md" mb={4}>
          {error}
        </Box>
      )}

      {loading ? (
        <Flex justify="center" p={10}>
          <Spinner size="xl" />
        </Flex>
      ) : (
        <>
          {overallStats && (
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                <Stat>
                  <StatLabel color="gray.500">Total Cases</StatLabel>
                  <StatNumber fontSize="2xl">{overallStats.totalCases}</StatNumber>
                </Stat>
              </Box>
              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                <Stat>
                  <StatLabel color="gray.500">Overall Naming Compliance</StatLabel>
                  <StatNumber fontSize="2xl" color={overallStats.namingCorrectRate == 100 ? "green.500" : "red.500"}>
                    {overallStats.namingCorrectRate}%
                  </StatNumber>
                  <Progress 
                    value={Number(overallStats.namingCorrectRate)} 
                    colorScheme={overallStats.namingCorrectRate == 100 ? "green" : "red"} 
                    size="xs" 
                    mt={2} 
                    borderRadius="md" 
                  />
                </Stat>
              </Box>
              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                <Stat>
                  <StatLabel color="gray.500">Overall Spine 5-Muscle Compliance</StatLabel>
                  <StatNumber fontSize="2xl" color={overallStats.muscleCorrectRate === 'N/A' ? "gray.500" : (overallStats.muscleCorrectRate == 100 ? "green.500" : "red.500")}>
                    {overallStats.muscleCorrectRate}{overallStats.muscleCorrectRate !== 'N/A' ? '%' : ''}
                  </StatNumber>
                  {overallStats.muscleCorrectRate !== 'N/A' && (
                    <Progress 
                      value={Number(overallStats.muscleCorrectRate)} 
                      colorScheme={overallStats.muscleCorrectRate == 100 ? "green" : "red"} 
                      size="xs" 
                      mt={2} 
                      borderRadius="md" 
                    />
                  )}
                </Stat>
              </Box>
            </SimpleGrid>
          )}

        <Tabs variant="enclosed" colorScheme="blue" index={tabIndex} onChange={(index) => setTabIndex(index)}>
          <TabList>
            <Tab>Raw Data</Tab>
            <Tab>Naming Summary</Tab>
            <Tab>Spine 5-Muscle Summary</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0} py={4}>
              <Box overflowX="auto" bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Case ID</Th>
                      <Th>Case Description<br/><Text fontSize="xs" color="gray.500" fontWeight="normal">(Cadwell/Scheduling)</Text></Th>
                      <Th>Template Name<br/><Text fontSize="xs" color="gray.500" fontWeight="normal">(Summary Report)</Text></Th>
                      <Th>Version<br/><Text fontSize="xs" color="gray.500" fontWeight="normal">(Summary Report)</Text></Th>
                      <Th textAlign="center">Cadwell<br/>Data</Th>
                      <Th>Date of Surgery</Th>
                      <Th>Technologist</Th>
                      <Th>Surgeon</Th>
                      <Th>Facility</Th>
                      <Th>Category</Th>
                      <Th textAlign="center">Naming</Th>
                      <Th textAlign="center">5-Muscle</Th>
                      <Th>Failure Reason</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.length === 0 ? (
                      <Tr>
                        <Td colSpan={13} textAlign="center" py={4}>No data found for the selected filters.</Td>
                      </Tr>
                    ) : (
                      data.map((row, index) => (
                        <Tr key={index}>
                          <Td maxW="150px" isTruncated title={row.tech_report_case_id || row.external_case_id}>
                            <Text 
                              as="a" 
                              href={`/ionmform?caseid=${row.external_case_id}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              color="blue.500"
                              _hover={{ textDecoration: 'underline' }}
                            >
                              {row.tech_report_case_id || row.external_case_id}
                            </Text>
                          </Td>
                          <Td maxW="200px" isTruncated title={row.case_description}>
                            {row.case_description}
                          </Td>
                          <Td maxW="200px" isTruncated title={row.template_name || 'None Found'}>
                            {row.template_name === 'MISSING_REPORT' ? (
                              <Badge colorScheme="red" variant="subtle" textTransform="none" fontWeight="bold">
                                Missing Report
                              </Badge>
                            ) : row.template_name ? (
                              <Badge 
                                colorScheme={row.naming_compliant ? "blue" : "orange"} 
                                variant={row.naming_compliant ? "outline" : "subtle"} 
                                textTransform="none" 
                                fontWeight="normal"
                              >
                                {row.template_name}
                              </Badge>
                            ) : (
                              <Text color="gray.400" fontSize="xs">None Found</Text>
                            )}
                          </Td>
                          <Td>
                            {row.template_version ? (
                              <Badge colorScheme="purple" variant="subtle">
                                {row.template_version}
                              </Badge>
                            ) : (
                              <Text color="gray.400" fontSize="xs">-</Text>
                            )}
                          </Td>
                          <Td textAlign="center">
                            {row.has_cadwell_data ? (
                              <Badge colorScheme="green">Yes</Badge>
                            ) : (
                              <Badge colorScheme="red">No</Badge>
                            )}
                          </Td>
                          <Td>{row.date_of_surgery}</Td>
                          <Td>{row.technologist_name}</Td>
                          <Td>{row.referring_provider}</Td>
                          <Td maxW="150px" isTruncated title={row.facility}>{row.facility}</Td>
                          <Td>{row.procedure_category}</Td>
                          <Td textAlign="center">
                            {row.naming_compliant ? (
                              <Icon as={CheckCircleIcon} color="green.500" boxSize={5} />
                            ) : (
                              <Icon as={WarningIcon} color="red.500" boxSize={5} />
                            )}
                          </Td>
                          <Td textAlign="center">
                            {row.procedure_category === 'Spine' ? (
                              row.muscle_compliant ? (
                                <Icon as={CheckCircleIcon} color="green.500" boxSize={5} />
                              ) : (
                                <Icon as={WarningIcon} color="red.500" boxSize={5} />
                              )
                            ) : (
                              <Badge colorScheme="gray">N/A</Badge>
                            )}
                          </Td>
                          <Td maxW="300px" whiteSpace="normal">
                            <Text fontSize="xs" color="gray.600">
                              {row.muscle_justification}
                            </Text>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>

            <TabPanel px={0} py={4}>
              {dailySummaryData.length > 0 && (
                <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
                  <Heading size="sm" mb={4} color="gray.600">Naming Compliance Trend</Heading>
                  <Box height="250px">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={dailySummaryData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{fontSize: 12}} />
                        <YAxis domain={[0, 100]} tick={{fontSize: 12}} tickFormatter={(tick) => `${tick}%`} />
                        <Tooltip 
                          formatter={(value, name) => [`${value}%`, name === 'namingCorrectRate' ? 'Naming Compliance' : name]}
                          labelFormatter={(label) => `Date: ${label}`}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="namingCorrectRate" 
                          name="Naming Compliance" 
                          stroke="#3182ce" 
                          strokeWidth={2} 
                          dot={{ r: 4 }} 
                          activeDot={{ r: 6 }} 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              )}
              <Box overflowX="auto" bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Technologist</Th>
                      <Th isNumeric>Total Cases</Th>
                      <Th isNumeric>Naming Issues</Th>
                      <Th isNumeric>% Correct</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {namingSummaryData.length === 0 ? (
                      <Tr>
                        <Td colSpan={4} textAlign="center" py={4}>No data available.</Td>
                      </Tr>
                    ) : (
                      namingSummaryData.map((tech, index) => (
                        <Tr key={index}>
                          <Td fontWeight="medium">
                            <Text 
                              as="button" 
                              color="blue.500" 
                              _hover={{ textDecoration: 'underline' }} 
                              onClick={() => handleTechnologistClick(tech.name)}
                              textAlign="left"
                            >
                              {tech.name}
                            </Text>
                          </Td>
                          <Td isNumeric>{tech.totalCases}</Td>
                          <Td isNumeric>{tech.namingFailures}</Td>
                          <Td>
                            <Flex align="center" justify="flex-end" gap={3}>
                              <Text color={tech.namingCorrectRate == 100 ? "green.600" : "red.600"} fontWeight={tech.namingCorrectRate == 100 ? "normal" : "bold"} minW="45px" textAlign="right">
                                {tech.namingCorrectRate}%
                              </Text>
                              <Progress 
                                value={Number(tech.namingCorrectRate)} 
                                colorScheme={tech.namingCorrectRate == 100 ? "green" : "red"} 
                                size="sm" 
                                width="100px" 
                                borderRadius="md" 
                                bg="gray.100"
                              />
                            </Flex>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>

            <TabPanel px={0} py={4}>
              {dailySummaryData.filter(d => d.muscleCorrectRate !== null).length > 0 && (
                <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
                  <Heading size="sm" mb={4} color="gray.600">Spine 5-Muscle Compliance Trend</Heading>
                  <Box height="250px">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={dailySummaryData.filter(d => d.muscleCorrectRate !== null)} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{fontSize: 12}} />
                        <YAxis domain={[0, 100]} tick={{fontSize: 12}} tickFormatter={(tick) => `${tick}%`} />
                        <Tooltip 
                          formatter={(value, name) => [`${value}%`, name === 'muscleCorrectRate' ? '5-Muscle Compliance' : name]}
                          labelFormatter={(label) => `Date: ${label}`}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="muscleCorrectRate" 
                          name="5-Muscle Compliance" 
                          stroke="#805ad5" 
                          strokeWidth={2} 
                          dot={{ r: 4 }} 
                          activeDot={{ r: 6 }} 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              )}
              <Box overflowX="auto" bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Technologist</Th>
                      <Th isNumeric>Total Spine Cases</Th>
                      <Th isNumeric>5-Muscle Issues</Th>
                      <Th isNumeric>% Correct</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {spineSummaryData.length === 0 ? (
                      <Tr>
                        <Td colSpan={4} textAlign="center" py={4}>No Spine cases available.</Td>
                      </Tr>
                    ) : (
                      spineSummaryData.map((tech, index) => (
                        <Tr key={index}>
                          <Td fontWeight="medium">
                            <Text 
                              as="button" 
                              color="blue.500" 
                              _hover={{ textDecoration: 'underline' }} 
                              onClick={() => handleTechnologistClick(tech.name)}
                              textAlign="left"
                            >
                              {tech.name}
                            </Text>
                          </Td>
                          <Td isNumeric>{tech.totalCases}</Td>
                          <Td isNumeric>{tech.muscleFailures}</Td>
                          <Td>
                            <Flex align="center" justify="flex-end" gap={3}>
                              <Text color={tech.muscleCorrectRate == 100 ? "green.600" : "red.600"} fontWeight={tech.muscleCorrectRate == 100 ? "normal" : "bold"} minW="45px" textAlign="right">
                                {tech.muscleCorrectRate}%
                              </Text>
                              <Progress 
                                value={Number(tech.muscleCorrectRate)} 
                                colorScheme={tech.muscleCorrectRate == 100 ? "green" : "red"} 
                                size="sm" 
                                width="100px" 
                                borderRadius="md" 
                                bg="gray.100"
                              />
                            </Flex>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
        </>
      )}
      </Box>
    </Box>
  );
};

export default NMAComplianceDashboard;
